import {
  configureSearchDrawerProductCard,
  fetchAndCacheColorVariantProducts,
  hydrateSearchDrawerProductCards,
  setCachedProduct,
} from '@theme/search-drawer-product-card';

const LIST_SELECTOR = '[data-collection-product-row-list]';
const CARD_SELECTOR = '[data-collection-product-card]';
const PAYLOAD_SELECTOR = '[data-product-payload]';
const CONFIG_SELECTOR = '[data-collection-product-row-config]';
const PAGINATION_SELECTOR = '[data-collection-product-row-pagination]';
const ITEM_SELECTOR = '.collection-product-row__item';

/** @type {WeakMap<HTMLElement, AbortController>} */
const paginationControllers = new WeakMap();

/**
 * Returns list items used for row pagination.
 * @param {HTMLElement} list
 * @returns {HTMLElement[]}
 */
const getPaginationItems = (list) => {
  return Array.from(list.querySelectorAll(ITEM_SELECTOR)).filter(
    (item) => item instanceof HTMLElement && !item.classList.contains('collection-product-row__item--placeholder')
  );
};

/**
 * Updates the active dot for the current scroll position.
 * @param {HTMLElement} list
 * @param {HTMLElement} pagination
 */
const updatePaginationDots = (list, pagination) => {
  const items = getPaginationItems(list);
  if (!items.length) {
    pagination.hidden = true;
    return;
  }

  const isScrollable = list.scrollWidth > list.clientWidth + 1;
  pagination.hidden = !isScrollable;

  if (!isScrollable) return;

  const scrollLeft = list.scrollLeft;
  let activeIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;

  items.forEach((item, index) => {
    const distance = Math.abs(item.offsetLeft - scrollLeft);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      activeIndex = index;
    }
  });

  pagination.querySelectorAll('[data-collection-product-row-dot]').forEach((dot, index) => {
    if (!(dot instanceof HTMLButtonElement)) return;
    dot.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
  });
};

/**
 * Builds dot pagination for horizontally scrollable product rows.
 * @param {HTMLElement} section
 */
const setupCollectionProductRowPagination = (section) => {
  if (!section.hasAttribute('data-collection-product-row-dots')) return;

  const list = section.querySelector(LIST_SELECTOR);
  const pagination = section.querySelector(PAGINATION_SELECTOR);

  if (!(list instanceof HTMLElement) || !(pagination instanceof HTMLElement)) return;

  paginationControllers.get(section)?.abort();

  const controller = new AbortController();
  paginationControllers.set(section, controller);
  const { signal } = controller;

  const renderDots = () => {
    const items = getPaginationItems(list);
    pagination.replaceChildren();

    if (!items.length || list.scrollWidth <= list.clientWidth + 1) {
      pagination.hidden = true;
      return;
    }

    pagination.hidden = false;

    items.forEach((item, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'collection-product-row__pagination-dot';
      dot.setAttribute('data-collection-product-row-dot', '');
      dot.setAttribute('aria-label', `Show product ${index + 1}`);
      dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');

      dot.addEventListener(
        'click',
        () => {
          item.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        },
        { signal }
      );

      pagination.appendChild(dot);
    });

    updatePaginationDots(list, pagination);
  };

  const handleScroll = () => {
    window.requestAnimationFrame(() => {
      updatePaginationDots(list, pagination);
    });
  };

  list.addEventListener('scroll', handleScroll, { passive: true, signal });
  window.addEventListener('resize', renderDots, { signal });

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(renderDots);
    resizeObserver.observe(list);
    signal.addEventListener('abort', () => resizeObserver.disconnect());
  }

  renderDots();
};

/**
 * Parses a JSON script tag payload.
 * @param {Element|null} script
 * @returns {object|null}
 */
const parseJsonScript = (script) => {
  if (!(script instanceof HTMLScriptElement)) return null;

  try {
    return JSON.parse(script.textContent || '');
  } catch {
    return null;
  }
};

/**
 * Applies storefront config for collection product cards.
 * @param {HTMLElement} section
 */
const ensureCardConfig = (section) => {
  const configScript = section.querySelector(CONFIG_SELECTOR);
  const config = parseJsonScript(configScript) || {};

  configureSearchDrawerProductCard({
    shop: config.shop,
    storefrontAccessToken: config.storefrontAccessToken || 'c068f4855b00e4675bc5153b9564158d',
    moneyFormat: config.moneyFormat,
    currency: config.currency,
    country: config.country,
    icons: config.icons || {},
  });
};

/**
 * Returns true when the row contains product card payloads to hydrate.
 * @param {HTMLElement} section
 * @returns {boolean}
 */
const hasProductCardPayloads = (section) => {
  return Boolean(section.querySelector(`${CARD_SELECTOR} ${PAYLOAD_SELECTOR}`));
};

/**
 * Hydrates all product cards inside a collection product row.
 * @param {HTMLElement} section
 * @returns {Promise<void>}
 */
const hydrateSection = async (section) => {
  if (hasProductCardPayloads(section)) {
    await customElements.whenDefined('search-drawer-product-card');
    ensureCardConfig(section);

    const list = section.querySelector(LIST_SELECTOR);
    if (list instanceof HTMLElement) {
      const cards = list.querySelectorAll(CARD_SELECTOR);
      const productIds = [];

      cards.forEach((item) => {
        if (!(item instanceof HTMLElement)) return;

        const card = item.querySelector('search-drawer-product-card');
        const payloadScript = item.querySelector(PAYLOAD_SELECTOR);

        if (!(card instanceof HTMLElement)) return;

        const product = parseJsonScript(payloadScript);
        if (!product?.handle) return;

        if (product.id) {
          setCachedProduct(String(product.id), product);
          productIds.push(String(product.id));
        }

        card.product = product;
      });

      await fetchAndCacheColorVariantProducts(productIds);
      await hydrateSearchDrawerProductCards(list);
    }
  }

  setupCollectionProductRowPagination(section);
};

/**
 * Collection product row — hydrates search-drawer-product-card instances on connect.
 */
class CollectionProductRow extends HTMLElement {
  connectedCallback() {
    hydrateSection(this);
  }

  disconnectedCallback() {
    paginationControllers.get(this)?.abort();
    paginationControllers.delete(this);
  }
}

export { hydrateSection, hasProductCardPayloads, setupCollectionProductRowPagination };

if (!customElements.get('collection-product-row')) {
  customElements.define('collection-product-row', CollectionProductRow);
}
